import { api } from 'shared-web'
import type { WorkflowCreateRequest, WorkflowUpdateRequest, QueryParams } from '../types.ts'

export const workflowGalleryService = {
  // Get paginated workflows with filtering and search (from cached JSON)
  list: async (params?: QueryParams) => {
    const response = await api.get<any>('/v1/public/gallery/workflows.json')

    // The cached endpoint returns data directly, not wrapped in ApiResponse format
    const data = response.data || response

    // Client-side filtering/searching since it's cached data
    let filteredItems = data.items || []

    if (params?.q) {
      const searchTerm = params.q.toLowerCase()
      filteredItems = filteredItems.filter(
        (item: any) =>
          item.name?.toLowerCase().includes(searchTerm) ||
          item.title?.toLowerCase().includes(searchTerm) ||
          item.description?.toLowerCase().includes(searchTerm) ||
          item.tags?.some((tag: string) => tag.toLowerCase().includes(searchTerm))
      )
    }

    if (params?.category && params.category !== 'All Workflows') {
      filteredItems = filteredItems.filter((item: any) => item.category === params.category)
    }

    // Client-side pagination
    const page = params?.page || 1
    const limit = params?.limit || 12
    const start = (page - 1) * limit
    const paginatedItems = filteredItems.slice(start, start + limit)

    return {
      items: paginatedItems,
      total: filteredItems.length,
      page,
      limit,
      totalPages: Math.ceil(filteredItems.length / limit)
    }
  },

  // Get single workflow by ID (from cached data)
  getById: async (id: string) => {
    const response = await api.get<any>('/v1/public/gallery/workflows.json')
    const data = response.data || response
    const workflow = data.items?.find((item: any) => item.id === id)

    if (!workflow) {
      throw new Error(`Workflow with id ${id} not found`)
    }

    return workflow
  },

  // Create new workflow (redirect to authenticated app)
  create: async (data: WorkflowCreateRequest) => {
    throw new Error('Creating workflows requires authentication. Please log in to the dashboard.')
  },

  // Update workflow (redirect to authenticated app)
  update: async (data: WorkflowUpdateRequest) => {
    throw new Error('Updating workflows requires authentication. Please log in to the dashboard.')
  },

  // Delete workflow (redirect to authenticated app)
  delete: async (id: string) => {
    throw new Error('Deleting workflows requires authentication. Please log in to the dashboard.')
  },

  // Clone workflow (redirect to authenticated app)
  clone: async (id: string) => {
    throw new Error('Cloning workflows requires authentication. Please log in to the dashboard.')
  },

  // Get workflow categories (from cached data)
  getCategories: async () => {
    const response = await api.get<any>('/v1/public/gallery/workflows.json')
    const data = response.data || response

    // Extract unique categories from cached workflows
    const categories = new Set<string>()
    data.items?.forEach((item: any) => {
      if (item.category) {
        categories.add(item.category)
      }
    })

    return Array.from(categories).sort()
  },

  // Get featured workflows (from cached data)
  getFeatured: async (limit?: number) => {
    const response = await api.get<any>('/v1/public/gallery/workflows.json')
    const data = response.data || response

    // Filter featured workflows
    let featured = data.items?.filter((item: any) => item.isFeatured) || []

    // Apply limit if provided
    if (limit && limit > 0) {
      featured = featured.slice(0, limit)
    }

    return featured
  }
}
